/**
 * Expression Parser for Named Variable References
 * 
 * Handles parsing and transformation of expressions that use ${"variable name"} syntax
 */

// Regex to match ${"name"} or ${'name'} patterns
const VARIABLE_REFERENCE_REGEX = /\$\{["']([^"']+)["']\}/g;

/**
 * Extract all variable names referenced in an expression
 * @param expression The expression string containing ${"name"} references
 * @returns Array of unique variable names referenced in the expression
 */
export const extractVariableNames = (expression: string): string[] => {
    const names: string[] = [];
    const matches = expression.matchAll(VARIABLE_REFERENCE_REGEX);

    for (const match of matches) {
        const name = match[1];
        if (name && !names.includes(name)) {
            names.push(name);
        }
    }

    return names;
};

/**
 * Replace variable references with sanitized identifiers for evaluation
 * @param expression The expression containing ${"name"} references
 * @param variableNames Ordered list of variable names to replace
 * @returns Object with sanitized expression and mapping of identifiers to names
 */
export const replaceVariableReferences = (
    expression: string,
    variableNames: string[]
): { expression: string; identifierMap: Map<string, string> } => {
    const identifierMap = new Map<string, string>();
    let sanitizedExpression = expression;

    // Create sanitized identifiers for each variable
    variableNames.forEach((name, index) => {
        const identifier = `_var_${index}`;
        identifierMap.set(identifier, name);

        // Replace all occurrences of ${"name"} with the identifier
        const pattern = new RegExp(
            `\\$\\{["']${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}["']\\}`,
            'g'
        );
        sanitizedExpression = sanitizedExpression.replace(pattern, identifier);
    });

    return { expression: sanitizedExpression, identifierMap };
};

/**
 * Validate that an expression can be safely evaluated
 * @param expression The expression to validate
 * @returns Object with validation result and error message if invalid
 */
export const validateExpression = (expression: string): { valid: boolean; error?: string } => {
    if (!expression || expression.trim().length === 0) {
        return { valid: false, error: 'Expression cannot be empty' };
    }

    // Check for if/else statements (not allowed)
    const ifElsePattern = /\b(if|else)\s*\(/i;
    if (ifElsePattern.test(expression)) {
        return { valid: false, error: 'If-else statements are not allowed.' };
    }

    // Check for ternary operators (not allowed)
    const ternaryPattern = /\?[^:]*:/;
    if (ternaryPattern.test(expression)) {
        return { valid: false, error: 'Ternary operators (? :) are not allowed.' };
    }

    // Check for whitespace between ${ and " in variable references
    const whitespaceInReference = /\$\{\s+["']/;
    if (whitespaceInReference.test(expression)) {
        return { valid: false, error: 'Variable references must not have whitespace between ${ and ". Use ${"name"} not ${ "name"}' };
    }

    const referenceStarts = expression.match(/\$\{/g)?.length ?? 0;
    const references = expression.match(/\$\{[^}]*\}/g) ?? [];

    if (references.length !== referenceStarts) {
        return { valid: false, error: 'Incomplete variable reference. Use ${"name"} syntax.' };
    }

    const invalidReferences = references.filter((reference) => !/\$\{["'][^"']+["']\}/.test(reference));

    if (invalidReferences.length > 0) {
        return { valid: false, error: 'Incomplete variable reference. Use ${"name"} syntax.' };
    }

    return { valid: true };
};

/**
 * Build a map of variable names to node IDs from a node list
 * @param nodes Array of simulation nodes
 * @returns Map from node name to node ID
 */
export const buildNameToIdMap = (nodes: Array<{ id: string; data: { name: string } }>): Map<string, string> => {
    const nameToId = new Map<string, string>();

    nodes.forEach(node => {
        // For duplicate names, first occurrence wins
        if (!nameToId.has(node.data.name)) {
            nameToId.set(node.data.name, node.id);
        }
    });

    return nameToId;
};

/**
 * Resolve variable names to node IDs
 * @param variableNames Array of variable names from expression
 * @param nameToIdMap Map from node names to IDs
 * @returns Array of node IDs in same order as variableNames
 */
export const resolveVariableNamesToIds = (
    variableNames: string[],
    nameToIdMap: Map<string, string>
): string[] => {
    return variableNames.map(name => nameToIdMap.get(name) || '').filter(id => id !== '');
};

